// ============================================
// SettingsModal.jsx - User Settings Modal (Updated)
// ============================================
// Tabbed modal for: Usage, Profile, Plans, Textbooks
// Includes Stripe checkout integration

import React, { useState, useEffect } from 'react';
import {
  X,
  BarChart3,
  User,
  CreditCard,
  Check,
  Zap,
  Crown,
  Rocket,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  BookOpen,
  ExternalLink,
  Loader,
} from 'lucide-react';
import GhostLogo from './GhostLogo';
import TextbookUpload from './TextbookUpload';
import { stripeService, PLANS } from '../services/stripeService';

// ============================================
// USAGE TAB
// ============================================
function UsageTab({ user, usage }) {
  const usedToday = usage?.dailyLimit - usage?.remaining || 0;
  const percentUsed = usage?.dailyLimit ? (usedToday / usage.dailyLimit) * 100 : 0;
  const isUnlimited = usage?.unlimited || user?.tier === 'pilot_lifetime' || user?.tier === 'basic';

  return (
    <div className="settings-tab-content">
      <h3>Usage Overview</h3>
      
      {/* Today's Usage */}
      <div className="usage-card">
        <div className="usage-card-header">
          <span className="usage-card-title">Today's Messages</span>
          <span className="usage-card-value">
            {isUnlimited ? (
              <>∞ Unlimited</>
            ) : (
              <>{usedToday} / {usage?.dailyLimit || 0}</>
            )}
          </span>
        </div>
        {!isUnlimited && (
          <div className="usage-progress-bar">
            <div 
              className="usage-progress-fill"
              style={{ width: `${Math.min(100, percentUsed)}%` }}
            />
          </div>
        )}
        <p className="usage-card-subtitle">
          {isUnlimited 
            ? 'Enjoy unlimited access with your plan!' 
            : `${usage?.remaining || 0} messages remaining today`}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Zap size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{usage?.messagesToday || usage?.used_today || 0}</span>
            <span className="stat-label">Messages Today</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <BarChart3 size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{usage?.messagesTotal || user?.essays_total || 0}</span>
            <span className="stat-label">Total Messages</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Crown size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value capitalize">
              {user?.tier === 'pilot_lifetime' ? 'Lifetime' : user?.tier || 'Free'}
            </span>
            <span className="stat-label">Current Plan</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Rocket size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{usage?.sessionsCount || 0}</span>
            <span className="stat-label">Chat Sessions</span>
          </div>
        </div>
      </div>

      {/* Usage Reset Info */}
      <div className="usage-info-box">
        <AlertCircle size={16} />
        <span>Usage resets daily at midnight UTC</span>
      </div>
    </div>
  );
}

// ============================================
// PROFILE TAB
// ============================================
function ProfileTab({ user, onUpdateProfile, onChangePassword }) {
  const [username, setUsername] = useState(user?.username || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const result = await onUpdateProfile({ username, phone: phone || null });

    setSaving(false);
    if (result?.success) {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } else {
      setMessage({ type: 'error', text: result?.error || 'Failed to update profile' });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setSaving(true);
    const result = await onChangePassword(oldPassword, newPassword);
    setSaving(false);

    if (result?.success) {
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } else {
      setPasswordMessage({ type: 'error', text: result?.error || 'Failed to change password' });
    }
  };

  return (
    <div className="settings-tab-content">
      <h3>Profile Information</h3>

      {/* Profile Form */}
      <form onSubmit={handleSaveProfile} className="profile-form">
        {message && (
          <div className={`form-message ${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        <div className="form-group">
          <label>
            <Mail size={16} />
            Email
          </label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="disabled"
          />
          <span className="form-hint">Email cannot be changed</span>
        </div>

        <div className="form-group">
          <label>
            <User size={16} />
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Your username"
          />
        </div>

        <div className="form-group">
          <label>
            <Phone size={16} />
            Phone (optional)
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 000-0000"
          />
        </div>

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      {/* Password Section */}
      <div className="password-section">
        <h4>
          <Lock size={16} />
          Password
        </h4>

        {!showPasswordForm ? (
          <button
            className="change-password-btn"
            onClick={() => setShowPasswordForm(true)}
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handleChangePassword} className="password-form">
            {passwordMessage && (
              <div className={`form-message ${passwordMessage.type}`}>
                {passwordMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {passwordMessage.text}
              </div>
            )}

            <div className="form-group">
              <label>Current Password</label>
              <div className="password-input">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                >
                  {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div className="password-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowPasswordForm(false);
                  setOldPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Cancel
              </button>
              <button type="submit" className="save-btn" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Account Info */}
      <div className="account-info">
        <p>
          Member since: {user?.created_at 
            ? new Date(user.created_at).toLocaleDateString() 
            : 'N/A'}
        </p>
      </div>
    </div>
  );
}

// ============================================
// PLANS TAB (with Stripe integration)
// ============================================
function PlansTab({ user, onSelectPlan }) {
  const currentTier = user?.tier || 'free';
  const [loading, setLoading] = useState(null); // Track which plan is loading
  const [error, setError] = useState(null);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Get started with basic features',
      features: [
        '3 messages per day',
        'Basic chat history',
        'Standard response speed',
      ],
      icon: Zap,
      color: 'gray',
    },
    {
      id: 'basic',
      name: 'GhostInk Basic',
      price: '$5',
      period: '/month',
      description: 'Unlimited during pilot',
      features: [
        'Unlimited messages during pilot',
        'Full chat history',
        'All writing modes',
        'Priority support',
      ],
      icon: Rocket,
      color: 'blue',
      stripePlan: 'basic',
    },
    {
      id: 'pilot_lifetime',
      name: 'Pilot Lifetime',
      price: '$35.99',
      period: 'one-time',
      description: 'Early supporter special',
      features: [
        'Lifetime unlimited access',
        'All writing modes forever',
        'Early supporter badge',
        'All future features included',
        'Priority support forever',
      ],
      icon: Crown,
      color: 'green',
      popular: true,
      stripePlan: 'pilot_lifetime',
      badge: 'Limited Time',
    },
  ];

  const handleSelectPlan = async (plan) => {
    if (!plan.stripePlan) return;
    
    setLoading(plan.id);
    setError(null);

    try {
      const result = await stripeService.createCheckoutSession(plan.stripePlan);
      
      if (result.error) {
        setError(result.error);
        setLoading(null);
        return;
      }

      // Redirect to Stripe Checkout
      if (result.data?.checkout_url) {
        window.location.href = result.data.checkout_url;
      }
    } catch (e) {
      setError(e.message || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading('manage');
    setError(null);

    try {
      const result = await stripeService.createPortalSession();
      
      if (result.error) {
        setError(result.error);
        setLoading(null);
        return;
      }

      // Redirect to Stripe Customer Portal
      if (result.data?.portal_url) {
        window.location.href = result.data.portal_url;
      }
    } catch (e) {
      setError(e.message || 'Failed to open billing portal');
      setLoading(null);
    }
  };

  return (
    <div className="settings-tab-content plans-tab">
      <h3>Choose Your Plan</h3>
      <p className="plans-subtitle">
        Upgrade to unlock unlimited messages and features
      </p>

      {error && (
        <div className="form-message error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="plans-grid">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = currentTier === plan.id;
          const isLoading = loading === plan.id;
          
          return (
            <div
              key={plan.id}
              className={`plan-card ${plan.color} ${isCurrent ? 'current' : ''} ${plan.popular ? 'popular' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              {plan.badge && !plan.popular && <div className="limited-badge">{plan.badge}</div>}
              
              <div className="plan-header">
                <div className={`plan-icon ${plan.color}`}>
                  <Icon size={24} />
                </div>
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="period">{plan.period}</span>
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <Check size={16} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className={`plan-btn ${isCurrent ? 'current' : ''}`}
                onClick={() => !isCurrent && plan.stripePlan && handleSelectPlan(plan)}
                disabled={isCurrent || !plan.stripePlan || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader size={16} className="spinning" />
                    Processing...
                  </>
                ) : isCurrent ? (
                  'Current Plan'
                ) : plan.stripePlan ? (
                  <>
                    Upgrade
                    <ExternalLink size={14} />
                  </>
                ) : (
                  'Free Forever'
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Billing Info - Show for paid users */}
      {(user?.subscription_active || user?.tier === 'pilot_lifetime' || user?.tier === 'basic') && (
        <div className="billing-info">
          <h4>Billing</h4>
          <p>Manage your subscription and billing details</p>
          <button 
            className="manage-billing-btn" 
            onClick={handleManageBilling}
            disabled={loading === 'manage'}
          >
            {loading === 'manage' ? (
              <>
                <Loader size={14} className="spinning" />
                Opening...
              </>
            ) : (
              <>
                Manage Billing
                <ExternalLink size={14} />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// TEXTBOOKS TAB
// ============================================
function TextbooksTab({ onSelectTextbook }) {
  return (
    <div className="settings-tab-content">
      <TextbookUpload onSelectTextbook={onSelectTextbook} />
    </div>
  );
}

// ============================================
// MAIN SETTINGS MODAL
// ============================================
export default function SettingsModal({
  isOpen,
  onClose,
  user,
  usage,
  onUpdateProfile,
  onChangePassword,
  onSelectPlan,
  onSelectTextbook,
}) {
  const [activeTab, setActiveTab] = useState('usage');

  // Reset to usage tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('usage');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'usage', label: 'Usage', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'plans', label: 'Plans', icon: CreditCard },
    { id: 'textbooks', label: 'Textbooks', icon: BookOpen },
  ];

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-header">
          <div className="settings-title">
            <GhostLogo size={28} />
            <h2>Settings</h2>
          </div>
          <button className="settings-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="settings-content">
          {activeTab === 'usage' && <UsageTab user={user} usage={usage} />}
          {activeTab === 'profile' && (
            <ProfileTab
              user={user}
              onUpdateProfile={onUpdateProfile}
              onChangePassword={onChangePassword}
            />
          )}
          {activeTab === 'plans' && (
            <PlansTab user={user} onSelectPlan={onSelectPlan} />
          )}
          {activeTab === 'textbooks' && (
            <TextbooksTab onSelectTextbook={onSelectTextbook} />
          )}
        </div>
      </div>

      <style>{`
        .settings-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .settings-modal {
          background: var(--gi-gray-900, #111827);
          border: 1px solid var(--gi-gray-800, #1f2937);
          border-radius: 16px;
          width: 100%;
          max-width: 700px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .settings-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid var(--gi-gray-800, #1f2937);
        }

        .settings-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .settings-title h2 {
          margin: 0;
          font-size: 20px;
          color: var(--gi-gray-100, #f3f4f6);
        }

        .settings-close {
          background: transparent;
          border: none;
          color: var(--gi-gray-500, #6b7280);
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .settings-close:hover {
          background: var(--gi-gray-800, #1f2937);
          color: var(--gi-gray-300, #d1d5db);
        }

        .settings-tabs {
          display: flex;
          gap: 4px;
          padding: 12px 24px;
          border-bottom: 1px solid var(--gi-gray-800, #1f2937);
          overflow-x: auto;
        }

        .settings-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .settings-tab:hover {
          background: var(--gi-gray-800, #1f2937);
          color: var(--gi-gray-300, #d1d5db);
        }

        .settings-tab.active {
          background: var(--gi-green-900, #14532d);
          color: var(--gi-green-400, #4ade80);
        }

        .settings-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .settings-tab-content h3 {
          margin: 0 0 16px 0;
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 18px;
        }

        /* Usage Tab */
        .usage-card {
          background: var(--gi-gray-800, #1f2937);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .usage-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .usage-card-title {
          color: var(--gi-gray-400, #9ca3af);
          font-size: 14px;
        }

        .usage-card-value {
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 18px;
          font-weight: 600;
        }

        .usage-progress-bar {
          height: 8px;
          background: var(--gi-gray-700, #374151);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .usage-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--gi-green-600, #16a34a), var(--gi-green-500, #22c55e));
          border-radius: 4px;
          transition: width 0.3s;
        }

        .usage-card-subtitle {
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 12px;
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gi-green-900, #14532d);
          border-radius: 10px;
          color: var(--gi-green-400, #4ade80);
        }

        .stat-info {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 18px;
          font-weight: 600;
        }

        .stat-label {
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
        }

        .usage-info-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 8px;
          color: var(--gi-gray-400, #9ca3af);
          font-size: 13px;
        }

        /* Profile Tab */
        .profile-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--gi-gray-400, #9ca3af);
          font-size: 13px;
        }

        .form-group input {
          padding: 12px;
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 14px;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--gi-green-600, #16a34a);
        }

        .form-group input.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-hint {
          color: var(--gi-gray-600, #4b5563);
          font-size: 12px;
        }

        .form-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          border-radius: 8px;
          font-size: 13px;
        }

        .form-message.success {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .form-message.error {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .save-btn {
          padding: 12px 20px;
          background: linear-gradient(135deg, var(--gi-green-600, #16a34a), var(--gi-green-700, #15803d));
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .save-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
        }

        .save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .password-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--gi-gray-800, #1f2937);
        }

        .password-section h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 16px 0;
          color: var(--gi-gray-200, #e5e7eb);
          font-size: 15px;
        }

        .change-password-btn {
          padding: 10px 16px;
          background: transparent;
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .change-password-btn:hover {
          background: var(--gi-gray-700, #374151);
        }

        .password-form {
          background: var(--gi-gray-800, #1f2937);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .password-input {
          display: flex;
          align-items: center;
          background: var(--gi-gray-900, #111827);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          overflow: hidden;
        }

        .password-input input {
          flex: 1;
          border: none;
          background: transparent;
        }

        .password-input button {
          background: transparent;
          border: none;
          color: var(--gi-gray-500, #6b7280);
          padding: 12px;
          cursor: pointer;
        }

        .password-actions {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }

        .cancel-btn {
          flex: 1;
          padding: 10px;
          background: transparent;
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          color: var(--gi-gray-400, #9ca3af);
          font-size: 14px;
          cursor: pointer;
        }

        .password-actions .save-btn {
          flex: 1;
        }

        .account-info {
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid var(--gi-gray-800, #1f2937);
        }

        .account-info p {
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
          margin: 0;
        }

        /* Plans Tab */
        .plans-tab h3 {
          margin-bottom: 8px;
        }

        .plans-subtitle {
          color: var(--gi-gray-500, #6b7280);
          margin: 0 0 24px 0;
          font-size: 14px;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .plan-card {
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 12px;
          padding: 20px;
          position: relative;
          transition: all 0.2s;
        }

        .plan-card:hover {
          border-color: var(--gi-gray-600, #4b5563);
        }

        .plan-card.current {
          border-color: var(--gi-green-600, #16a34a);
        }

        .plan-card.popular {
          border-color: var(--gi-green-500, #22c55e);
        }

        .popular-badge, .limited-badge {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--gi-green-600, #16a34a);
          color: white;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 12px;
          white-space: nowrap;
        }

        .limited-badge {
          background: #f59e0b;
        }

        .plan-header {
          text-align: center;
          margin-bottom: 16px;
        }

        .plan-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }

        .plan-icon.gray {
          background: var(--gi-gray-700, #374151);
          color: var(--gi-gray-400, #9ca3af);
        }

        .plan-icon.blue {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
        }

        .plan-icon.green {
          background: var(--gi-green-900, #14532d);
          color: var(--gi-green-400, #4ade80);
        }

        .plan-header h4 {
          margin: 0 0 8px 0;
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 16px;
        }

        .plan-price {
          margin-bottom: 8px;
        }

        .plan-price .price {
          font-size: 24px;
          font-weight: 700;
          color: var(--gi-gray-100, #f3f4f6);
        }

        .plan-price .period {
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
        }

        .plan-description {
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
          margin: 0;
        }

        .plan-features {
          list-style: none;
          padding: 0;
          margin: 0 0 16px 0;
        }

        .plan-features li {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 12px;
          padding: 5px 0;
        }

        .plan-features li svg {
          color: var(--gi-green-500, #22c55e);
          flex-shrink: 0;
        }

        .plan-btn {
          width: 100%;
          padding: 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          background: var(--gi-gray-700, #374151);
          color: var(--gi-gray-300, #d1d5db);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .plan-btn:hover:not(:disabled) {
          background: var(--gi-gray-600, #4b5563);
        }

        .plan-card.popular .plan-btn:not(.current):not(:disabled) {
          background: linear-gradient(135deg, var(--gi-green-600, #16a34a), var(--gi-green-700, #15803d));
          color: white;
        }

        .plan-btn.current {
          background: var(--gi-green-900, #14532d);
          color: var(--gi-green-400, #4ade80);
          cursor: default;
        }

        .plan-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .billing-info {
          margin-top: 24px;
          padding: 20px;
          background: var(--gi-gray-800, #1f2937);
          border-radius: 12px;
        }

        .billing-info h4 {
          margin: 0 0 8px 0;
          color: var(--gi-gray-200, #e5e7eb);
        }

        .billing-info p {
          margin: 0 0 16px 0;
          color: var(--gi-gray-500, #6b7280);
          font-size: 14px;
        }

        .manage-billing-btn {
          padding: 10px 16px;
          background: transparent;
          border: 1px solid var(--gi-gray-600, #4b5563);
          border-radius: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .manage-billing-btn:hover:not(:disabled) {
          background: var(--gi-gray-700, #374151);
        }

        .manage-billing-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Spinning animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        .capitalize {
          text-transform: capitalize;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .plans-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          .settings-modal {
            max-height: 95vh;
            margin: 10px;
          }

          .settings-tabs {
            padding: 8px 16px;
            overflow-x: auto;
          }

          .settings-tab span {
            display: none;
          }

          .settings-content {
            padding: 16px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
